import { Input, Text, VStack } from '@chakra-ui/react'
import { ReactNode, useState } from 'react'

import { FormModal } from '../components/FormModal'

interface IPaymentDateModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (date: string) => void | Promise<void>
  defaultDate?: string
  isLoading?: boolean
  /**
   * `false` entrega o fechamento a quem chama, que fecha só depois do sucesso.
   *
   * Com o default `true` o modal some no mesmo instante em que confirma, então o `isLoading`
   * nunca chega a aparecer e um erro de rede leva junto a data escolhida. Continua sendo o
   * padrão porque o PayoutModal depende dele.
   */
  closeOnConfirm?: boolean
  /** Sobrescreve o título quando a tela precisa dizer de qual pagamento se trata. */
  title?: string
  /** Texto acima do campo — cabe um resumo do que está sendo pago. */
  description?: ReactNode
}

function todayAsDateString() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Reusable modal component for selecting a payment date.
 * Used by PayoutModal to allow users to choose when a payment was made.
 */
export default function PaymentDateModal({
  isOpen,
  onClose,
  onConfirm,
  defaultDate,
  isLoading = false,
  title = 'Selecionar Data de Pagamento',
  description = 'Escolha a data em que o pagamento foi realizado:',
  closeOnConfirm = true
}: IPaymentDateModalProps) {
  const [selectedDate, setSelectedDate] = useState(defaultDate || todayAsDateString())

  const handleConfirm = async () => {
    if (!closeOnConfirm) {
      await onConfirm(selectedDate)

      return
    }

    onConfirm(selectedDate)
    onClose()
  }

  const handleClose = () => {
    setSelectedDate(defaultDate || todayAsDateString())
    onClose()
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      onSubmit={handleConfirm}
      size="compact"
      /* Enquanto salva, não dá para fechar: a chamada já está em curso e sair no meio deixaria a
         tela de trás sem saber se a baixa entrou. Cobre Esc, clique no overlay e o X. */
      isDismissible={!isLoading}
      confirmLabel="Confirmar"
      isLoading={isLoading}
      isConfirmDisabled={!selectedDate}
    >
      <VStack spacing={4} align="stretch">
        <Text variant="caption">{description}</Text>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          isDisabled={isLoading}
        />
      </VStack>
    </FormModal>
  )
}
